/**
 * Central IDX Broker configuration.
 *
 * All IDX widget IDs and the account subdomain live here so they can be
 * swapped without touching component code. Fill these values from your
 * IDX Broker Control Panel:
 *
 *   Design → Widgets → New → (create widget) → Copy Code
 *
 * The widget ID is the numeric/alphanumeric slug at the end of the widget
 * script `src` (e.g. `.../idx/widgets/12345`). Only "New" widgets are
 * supported by this integration — Legacy widgets rely on `document.write`
 * and will not mount inside a React SPA.
 *
 * `accountSubdomain` is the leading label of your IDX Broker domain — for
 * example, if your IDX search lives at `homekey.idxbroker.com`, then
 * `accountSubdomain = "homekey"`.
 *
 * Leave any widget ID as `""` and the <IdxWidget /> component will render
 * a tasteful on-brand placeholder in its place, so layouts stay intact
 * before real IDs are wired in.
 *
 * This account is on the IDX Broker Core plan, whose "New" widget system does
 * not include a standalone quick/advanced search-form widget (the AI Smart
 * Search widget is Engage-only). So widget 165616 is the "Map Search Prime"
 * widget, which is the only SPA-safe interactive search available on this plan;
 * it is intentionally reused for quickSearch, advancedSearch, and mapSearch.
 * Widget 165613 is the "Featured Showcase" (Listings Showcase). savedSearch has
 * no SPA-safe equivalent on Core, so it stays empty and will render the
 * on-brand placeholder.
 */
export const IDX = {
  /** IDX Broker account subdomain, e.g. "homekey" in homekey.idxbroker.com */
  accountSubdomain: "johnsellsnj" as string,

  /**
   * Widget IDs from the IDX Broker Control Panel.
   * (Design → Widgets → New → Copy Code — grab the numeric ID at the end
   * of the script `src`.)
   */
  widgets: {
    /** Prime Map Search — the full interactive search (Search page only) */
    mapSearch: "165616" as string,
    /** Listings Showcase, grid (Listings page) */
    featuredShowcase: "165613" as string,
    /** Sold/Pending Showcase, grid (Sell page) */
    soldShowcase: "165614" as string,
    /** Featured Slide Show, carousel (Home page) */
    featuredCarousel: "165615" as string,
  },
} as const;

/**
 * Build the widget script URL. Protocol-relative on purpose so the widget
 * inherits the page scheme (works on both http and https previews).
 */
export const idxWidgetSrc = (id: string): string =>
  `//${IDX.accountSubdomain}.idxbroker.com/idx/widgets/${id}`;

/** Convenience predicate — true when configuration is real, not placeholder. */
export const isIdxConfigured = (widgetId: string): boolean =>
  widgetId.trim() !== "" && IDX.accountSubdomain !== "REPLACE_ME";

/**
 * Per-city / per-county Listings Showcase widget IDs.
 * Each key is a community slug from src/data/njCommunities.ts, mapped to the
 * IDX Broker "Listings Showcase" widget whose LOCATION filter is pre-baked to
 * that city/county server-side. These render real, live MLS listings inline.
 */
export const communityWidgets: Record<string, string> = {
  "princeton": "166279",
  "west-windsor": "166280",
  "hamilton": "166281",
  "robbinsville": "166282",
  "lawrenceville": "166283",
  "trenton": "166284",
  "monmouth-county": "166285",
  "ocean-county": "166286",
  "middlesex-county": "166287",
};

/** Return the pre-filtered Listings Showcase widget ID for a community slug, or "" if none. */
export const communityWidgetId = (slug: string): string =>
  communityWidgets[slug] ?? "";
